'use client'

/**
 * FilingsList — the recent-SEC-filings list, shared by the SSR /stocks/[symbol] page and the
 * DueDiligencePanel on the chart page. ONE implementation: the two surfaces differ only in density.
 *
 * ── WHY IT IS SHAPED THIS WAY (owner 2026-08-07) ──────────────────────────────────────────────────
 * "should we separate insider filings, financials and news, tab them up so we don't get too noisy with
 * the insider forms... what is easiest way to read and navigate the forms people are looking for."
 *
 * The instinct is right and the measurement backs it. Live form mix of the last 8 filings:
 *   NAMI  6 insider (4, 4/A) · 1 ownership · 1 material   -> 75% of the list was noise
 *   AAPL  4 insider · 4 everything else                   -> 50%
 *   CLRO  1 insider · 7 everything else                   -> a real news week looks nothing like it
 * On NAMI the two rows a trader actually wants were buried under six that say the same thing.
 *
 * ⛔ TABS WERE THE WRONG ANSWER, and it is the same mistake the catalyst field made. A tab is a
 * DEFAULT-HIDDEN drawer: it decides for the reader which filings they were allowed to see. The entire
 * reason this list exists is that a materiality gate hid the owner's 13G/A behind exactly that kind of
 * decision. So the noise is cut WITHOUT hiding anything:
 *
 *   1. ROLL UP duplicates (server-side). NAMI filed THREE `4/A` on one day and AAPL TWO `4` on one day.
 *      Identical rows carry no information and cost slots. One row, "x3". This alone removes most of
 *      the noise, and it removes it for every reader whether or not they touch a control.
 *   2. GROUP CHIPS, defaulting to ALL. Narrowing is opt-in and one click, and the counts are on the
 *      chips so you can see there ARE 6 insider filings without having to look at them.
 *   3. SCAN DEEPER than we render (6x, capped 90) so narrowing never lands on an empty list.
 *
 * ⚠ A chip with a zero count is not rendered — an empty group is a dead end, not a lens.
 */

import { useMemo, useState } from 'react'

export type FilingWeight = 'high' | 'medium' | 'low'
export type FilingGroup = 'events' | 'ownership' | 'offerings' | 'governance'
export type FilingRow = {
  form: string; date: string; url: string; label: string; summary: string
  weight: FilingWeight; group: FilingGroup; count?: number
}

// Weight is ADVISORY and hides nothing. It exists so a Form 4 does not read with the same authority as
// a 10-Q. NOT calibrated against forward returns -- do not trade "High" on its own.
const WEIGHT_META: Record<FilingWeight, { label: string; cls: string; help: string }> = {
  high: { label: 'High', cls: 'border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300', help: 'Form type that can reprice a stock on its own: periodic report, merger, activist 5% stake, delisting, or an 8-K carrying a repricing item.' },
  medium: { label: 'Med', cls: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300', help: 'Real disclosure, usually context rather than a mover: routine 8-K, registration, proxy, passive 5% stake.' },
  low: { label: 'Low', cls: 'border-border bg-muted text-muted-foreground', help: 'Housekeeping: insider forms, exhibits, certifications, holdings reports.' },
}

// Plain-English group names. "Financials" is deliberately NOT a group: 10-Q/10-K ARE the financials and
// they are also the biggest events, so splitting them out would put the most important filing a company
// files behind a second click.
const GROUP_META: { key: FilingGroup; label: string; help: string }[] = [
  { key: 'events', label: 'Events & reports', help: '8-K, 6-K, 10-Q, 10-K, 20-F, merger communications. What the company did, and its financials.' },
  { key: 'ownership', label: 'Insider & ownership', help: 'Forms 3/4/5, 13D/13G, Rule 144. Who is buying and selling.' },
  { key: 'offerings', label: 'Offerings', help: 'S-1, S-3, 424B prospectuses, shelf registrations. Potential dilution.' },
  { key: 'governance', label: 'Governance', help: 'Proxies, tender offers, listing certifications, delisting notices.' },
]

const fmtDate = (s: string, withYear: boolean) => {
  const ms = Date.parse(s.includes('T') ? (s.endsWith('Z') ? s : `${s}Z`) : `${s}T00:00:00Z`)
  if (!Number.isFinite(ms)) return ''
  return new Date(ms).toLocaleDateString('en-US', withYear
    ? { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }
    : { timeZone: 'UTC', month: 'short', day: 'numeric' })
}

export default function FilingsList({ filings, catalystUrl, dense = false }: {
  filings: FilingRow[]
  catalystUrl?: string
  /** Chart-panel variant: smaller type, no year in dates. */
  dense?: boolean
}) {
  const [group, setGroup] = useState<FilingGroup | 'all'>('all')
  const [open, setOpen] = useState(false)

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const f of filings) c[f.group] = (c[f.group] ?? 0) + 1
    return c
  }, [filings])

  const shown = group === 'all' ? filings : filings.filter((f) => f.group === group)
  // Only offer chips for groups that actually have rows. A control that leads nowhere is worse than no
  // control -- and with one group present, chips would just be a label.
  const chips = GROUP_META.filter((g) => (counts[g.key] ?? 0) > 0)
  if (!filings.length) return null

  const t = dense
    ? { row: 'text-[11.5px]', date: 'w-14 text-[10.5px]', form: 'w-20', formTxt: 'px-1 py-px text-[10px]', pad: 'px-2.5 py-1.5', head: 'text-[11px]' }
    : { row: 'text-[12.5px]', date: 'w-24 text-[11px]', form: 'w-24', formTxt: 'px-1.5 py-0.5 text-[11px]', pad: 'px-3 py-2', head: 'text-[12px]' }

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 ${t.pad} ${t.head} font-semibold text-muted-foreground transition-colors hover:text-foreground`}
      >
        <span>Recent SEC filings ({filings.length})</span>
        <span className="hidden font-normal text-muted-foreground sm:inline">every form, newest first</span>
        <span className="ml-auto text-muted-foreground">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="border-t border-border">
          {chips.length > 1 && (
            <div className={`flex flex-wrap items-center gap-1.5 border-b border-border ${t.pad}`}>
              <button
                type="button" onClick={() => setGroup('all')}
                className={`rounded-md border px-2 py-0.5 text-[11px] font-bold transition-colors ${group === 'all' ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-border bg-muted text-muted-foreground hover:text-foreground'}`}
              >All {filings.length}</button>
              {chips.map((g) => (
                <button
                  key={g.key} type="button" title={g.help} onClick={() => setGroup(g.key)}
                  className={`rounded-md border px-2 py-0.5 text-[11px] font-bold transition-colors ${group === g.key ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-border bg-muted text-muted-foreground hover:text-foreground'}`}
                >{g.label} {counts[g.key]}</button>
              ))}
            </div>
          )}

          {shown.map((f, i) => {
            const w = WEIGHT_META[f.weight] ?? WEIGHT_META.low
            return (
              <a
                key={`${f.url}-${i}`} href={f.url} target="_blank" rel="noopener noreferrer"
                className={`flex items-center gap-2 ${t.pad} ${t.row} transition-colors hover:bg-accent dark:hover:bg-zinc-800/60 ${i > 0 ? 'border-t border-border' : ''} ${f.url === catalystUrl ? 'bg-sky-500/[0.06]' : ''}`}
              >
                <span className={`${t.date} shrink-0 tabular-nums text-muted-foreground`}>{fmtDate(f.date, !dense)}</span>
                <span className={`${t.form} shrink-0`}>
                  <span className={`inline-block max-w-full truncate rounded bg-muted ${t.formTxt} font-mono font-bold text-foreground/80`} title={f.label}>{f.form}</span>
                </span>
                <span className="min-w-0 flex-1 truncate text-foreground" title={f.label}>
                  {f.summary}
                  {/* A rolled-up run says how many it stands for, so nothing looks dropped. */}
                  {/* Boolean guard, not a truthy number: `{f.count && ...}` renders a literal "0"
                      when count is 0, because React prints 0 where it ignores false/null. Same
                      pitfall that put a stray digit beside the navbar avatar. */}
                  {(f.count ?? 0) > 1 && (
                    <span className="ml-1.5 rounded bg-muted px-1 py-px text-[10px] font-bold text-muted-foreground" title={`${f.count} ${f.form} filings on this date, rolled into one row`}>x{f.count}</span>
                  )}
                </span>
                <span title={w.help} className={`shrink-0 cursor-help rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${w.cls}`}>{w.label}</span>
              </a>
            )
          })}

          <p className={`border-t border-border bg-muted/40 ${t.pad} text-[10px] leading-snug text-muted-foreground`}>
            Weight is a guide to the form type, not a trading signal. Nothing is filtered out: the groups
            narrow the view, they never decide what you can see.
          </p>
        </div>
      )}
    </div>
  )
}
